import React, { useEffect, useState } from "react";
import { ContentGrid } from "@/components/content/ContentGrid";
import { ContentItem, ContentType } from "@/types/content";
import { supabase } from "@/integrations/supabase/client";
import { useContentDelete } from "@/hooks/useContentDelete";
import { useRooms } from "@/hooks/useRooms";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ContentShareDialog } from "@/components/content/ContentShareDialog";
import { toast } from "sonner";
import { useTranslation } from "@/context/LanguageContext";

export const RecentsSection = ({ initialLimit = 5 }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showMore, setShowMore] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<ContentItem | null>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [itemToShare, setItemToShare] = useState<ContentItem | null>(null);
  const { deleteContent, deleting } = useContentDelete();
  const { rooms } = useRooms();

  const limit = showMore ? 20 : initialLimit;

  useEffect(() => {
    load();

    // Listen for upload events
    const handleContentUploaded = () => {
      load();
    };

    window.addEventListener("content-uploaded", handleContentUploaded);

    // Set up realtime subscription for new content
    const channel = supabase
      .channel("recents-content-changes")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "content",
        },
        (payload) => {
          console.log("New content created:", payload);
          const newItem: ContentItem = {
            id: payload.new.id,
            title: payload.new.title,
            type: payload.new.type as ContentType,
            thumbnailUrl: payload.new.thumbnail_url,
            createdAt: payload.new.created_at,
            visibility: payload.new.visibility as "private" | "public",
          };
          setItems((prev) => [newItem, ...prev.slice(0, limit - 1)]);
        }
      )
      .subscribe();

    return () => {
      window.removeEventListener("content-uploaded", handleContentUploaded);
      supabase.removeChannel(channel);
    };
  }, [showMore]);

  const load = async () => {
    setLoading(true);

    const { data } = await supabase
      .from("content")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(limit);

    const mapped =
      data?.map((c: any) => ({
        id: c.id,
        title: c.title,
        type: c.type as ContentType,
        thumbnailUrl: c.thumbnail_url,
        createdAt: c.created_at,
        visibility: c.visibility as "private" | "public",
      })) ?? [];

    setItems(mapped);
    setLoading(false);
  };

  const handleDeleteClick = (item: ContentItem) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    const success = await deleteContent(itemToDelete.id);
    if (success) {
      setItems(prev => prev.filter(item => item.id !== itemToDelete.id));
    }
    
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleShareClick = (item: ContentItem) => {
    setItemToShare(item);
    setShareDialogOpen(true);
  };

  const handleVisibilityChange = (contentId: string, visibility: "private" | "public") => {
    setItems(prev =>
      prev.map(item =>
        item.id === contentId ? { ...item, visibility } : item
      )
    );
  };

  const handleAddToRoom = async (item: ContentItem, roomId: string) => {
    const { error } = await supabase
      .from("content")
      .update({ room_id: roomId })
      .eq("id", item.id);

    if (error) {
      toast.error("Failed to add content to room");
      return;
    }

    const room = rooms.find(r => r.id === roomId);
    toast.success(`Added to ${room?.name || "room"}`);
    
    // Update local state
    setItems(prev =>
      prev.map(i =>
        i.id === item.id ? { ...i, roomId } : i
      )
    );
  };

  // Hide entire section when there's no content
  if (!loading && items.length === 0) {
    return null;
  }

  return (
    <section className="mt-10 w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-lg font-semibold text-foreground">{t("navigation.recents")}</p>
        <a href="/history" className="text-sm text-primary/80 hover:text-primary">
          {t("dashboard.viewAll")}
        </a>
      </div>

      {/* Grid */}
      <ContentGrid
        items={items}
        isLoading={loading}
        skeletonCount={initialLimit}
        rooms={rooms}
        onAddToRoom={handleAddToRoom}
        onShare={handleShareClick}
        onDelete={handleDeleteClick}
      />

      {/* Show More */}
      {items.length > 0 && (
        <div className="flex justify-center mt-6">
          <button
            onClick={() => setShowMore((p) => !p)}
            className="text-primary/80 hover:text-primary text-sm"
          >
            {showMore ? t("dashboard.showLess") : t("dashboard.showMore")}
          </button>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Content</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{itemToDelete?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Share Dialog */}
      <ContentShareDialog
        content={itemToShare}
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        onVisibilityChange={handleVisibilityChange}
      />
    </section>
  );
};
